import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertTriangle, RefreshCw, ShieldCheck, Truck, MapPin, ChevronRight, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TONES = {
  needs_manual_intervention: 'bg-red-500/15 text-red-300 border-red-500/40',
  assigned: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  in_transit: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  delivered: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  default: 'bg-zinc-700/40 text-zinc-300 border-zinc-600',
};
const tone = (s) => STATUS_TONES[s] || STATUS_TONES.default;

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const AdminLoadsPage = () => {
  const [needsIntervention, setNeedsIntervention] = useState([]);
  const [allLoads, setAllLoads] = useState([]);
  const [active, setActive] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/loads/needs-intervention`),
        axios.get(`${API}/loads?limit=50`),
      ]);
      setNeedsIntervention(a.data.loads || []);
      setAllLoads(b.data.loads || b.data || []);
    } catch (e) {
      toast.error('Failed to load loads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCoverage = async (l) => {
    setActive(l);
    try {
      const r = await axios.get(`${API}/admin/loads/${l.id}/coverage`);
      setCoverage(r.data);
    } catch {
      setCoverage(null);
      toast.error('Could not load coverage detail');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="admin-loads-page">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Loads</h1>
              {needsIntervention.length > 0 && (
                <Badge className="bg-red-500/15 text-red-300 border-red-500/40"
                       data-testid="needs-intervention-count">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {needsIntervention.length} need intervention
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Primary + backup carrier visibility, coverage timeline, and re-dispatch history.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-loads-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <Tabs defaultValue="intervention">
          <TabsList data-testid="loads-tabs">
            <TabsTrigger value="intervention" data-testid="tab-intervention">
              Needs Manual Intervention
              {needsIntervention.length > 0 && (
                <Badge className="ml-2 bg-red-500/20 text-red-300 border-red-500/30">
                  {needsIntervention.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All loads</TabsTrigger>
          </TabsList>

          <TabsContent value="intervention" className="mt-4">
            <LoadList loads={needsIntervention} onSelect={openCoverage}
                      emptyMessage="🎉 No loads need manual intervention." />
          </TabsContent>
          <TabsContent value="all" className="mt-4">
            <LoadList loads={allLoads} onSelect={openCoverage}
                      emptyMessage="No loads yet." />
          </TabsContent>
        </Tabs>

        {active && coverage && (
          <Card data-testid="coverage-detail-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    Load <code className="text-blue-400">#{active.id.slice(0, 8)}</code>
                    <Badge className={tone(coverage.status)}>{coverage.status}</Badge>
                    {coverage.needs_manual_intervention && (
                      <Badge className="bg-red-500/15 text-red-300 border-red-500/40">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Manual intervention
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                    <MapPin className="w-3 h-3" /> {active.origin} → {active.destination}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setActive(null); setCoverage(null); }}>
                  Close
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="carrier-slots">
                <CarrierSlot
                  testId="slot-primary"
                  title="Primary"
                  active={coverage.active_slot === 'primary'}
                  carrier={coverage.primary}
                  showRate
                />
                <CarrierSlot
                  testId="slot-backup-1"
                  title="Backup #1"
                  active={coverage.active_slot === 'backup_carrier_1'}
                  carrier={coverage.backup_carrier_1}
                />
                <CarrierSlot
                  testId="slot-backup-2"
                  title="Backup #2"
                  active={coverage.active_slot === 'backup_carrier_2'}
                  carrier={coverage.backup_carrier_2}
                />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Coverage timeline</div>
                <div className="border border-border rounded-md p-3 space-y-2 max-h-56 overflow-y-auto"
                     data-testid="coverage-timeline">
                  {(coverage.coverage_timeline || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No events.</p>
                  ) : (
                    coverage.coverage_timeline.slice().reverse().map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-zinc-500 shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium">{t.event}</span>
                          {t.driver_email && <span className="text-muted-foreground"> · {t.driver_email}</span>}
                          {t.reason && <span className="text-red-400 ml-2">{t.reason}</span>}
                        </div>
                        <span className="text-muted-foreground text-[10px] flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmtTime(t.at)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

const LoadList = ({ loads, onSelect, emptyMessage }) => (
  loads.length === 0
    ? <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">{emptyMessage}</CardContent></Card>
    : (
      <div className="space-y-2">
        {loads.map((l) => (
          <Card key={l.id} className="hover:border-blue-500/40 cursor-pointer transition-colors"
                onClick={() => onSelect(l)}
                data-testid={`load-row-${l.id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <Badge className={tone(l.status)}>{l.status}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {l.origin} → {l.destination}
                </div>
                <div className="text-xs text-muted-foreground">
                  {l.assigned_driver_email || 'unassigned'} ·{' '}
                  <code className="text-blue-400">#{l.id.slice(0, 8)}</code>
                </div>
              </div>
              {l.rate && <div className="text-sm text-emerald-400 font-semibold tabular-nums">
                ${Number(l.rate).toLocaleString()}
              </div>}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
);

const CarrierSlot = ({ title, carrier, active, showRate, testId }) => (
  <div className={`border rounded-md p-3 ${active ? 'border-blue-500/50 bg-blue-500/5' : 'border-zinc-700'}`}
       data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {active && <ShieldCheck className="w-3 h-3 text-blue-400" />} {title}
      </div>
      {active && <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 text-[10px]">ACTIVE</Badge>}
    </div>
    {carrier?.driver_email ? (
      <>
        <div className="text-sm font-medium truncate">{carrier.driver_name || carrier.driver_email}</div>
        <div className="text-xs text-muted-foreground truncate">{carrier.driver_email}</div>
        {showRate && carrier.accepted_rate ? (
          <div className="text-sm text-emerald-400 mt-1">${Number(carrier.accepted_rate).toLocaleString()}</div>
        ) : carrier.bid_amount ? (
          <div className="text-sm text-emerald-400 mt-1">${Number(carrier.bid_amount).toLocaleString()}</div>
        ) : null}
      </>
    ) : (
      <div className="text-xs text-muted-foreground italic">— No carrier in this slot —</div>
    )}
  </div>
);

export default AdminLoadsPage;
